import {DescriptionCardImageModel, DescriptionCardInfo} from './description-card-info';
import * as moment from 'moment';
import {ImmutableListRepositoryService} from '../../../../lib/gelios-field/immutable-list-repository.service';
import {MainLightboxService} from '../../../layout/main-lightbox.service';
import {Unit, UnitImagesShort} from '../../../dashboard/monitoring/unit';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {OrderPhotosLightboxService} from '../../../layout/order-photos-lightbox/order-photos-lightbox.service';
import {Subscription} from 'rxjs';
import {OnDestroy} from '@angular/core';
import {timestampToDateTime} from '../../../utility/timestamp-functions';
import {TranslateService} from '@ngx-translate/core';
import {RouteLibraryEntities} from '../../../route/route-library-entity';
import {RouteLibraryService} from '../../../route/route-library.service';
import {AuthorizationService} from '../../../authorization/authorization.service';
import {UserModel} from '../../../user/user-list/user.model';
import {UserLinkService} from '../../../user/user-link.service';

export abstract class DescriptionCardInfoComponent implements OnDestroy {
  subscriptions: Subscription[] = [];
  abstract imageSrc: DescriptionCardImageModel;
  abstract info: DescriptionCardInfo;
  abstract imagesShort: Array<UnitImagesShort>;

  protected constructor(
    protected immutableListRepositoryService: ImmutableListRepositoryService,
    protected mainLightboxService: MainLightboxService,
    protected http: HttpClient,
    protected orderPhotosLightboxService: OrderPhotosLightboxService,
    protected translates: TranslateService,
    protected routeLibrary: RouteLibraryService,
    protected userLinkService: UserLinkService,
    protected authorizationService: AuthorizationService,
  ) {
  }

  getValidValue(data: any) {
    return data !== null && data !== undefined && data !== '' ? data : '';
  }

  clickableImage(url: string) {
    return url && !url.startsWith('assets');
  }

  formatTimestampToDate(timestamp: number) {
    return timestamp ? moment.unix(timestamp).format('DD.MM.YYYY, HH:mm') : '';
  }

  paymentMethod(data: number[]) {
    return data && data.length > 0 ? data.map(
      id => this.immutableListRepositoryService.getItemById('end-user-order-payment-method', id)
    ).join(' + ') : '';
  }

  onImageClick() {
    if (this.clickableImage(this.imageSrc.image)) {
      const srcImage = {
        src: this.imageSrc.image,
        id: 0,
      };
      const onImageClientClick = [srcImage];
      this.mainLightboxService.open(onImageClientClick);
    }
  }

  openOrderLightbox(orderId: number) {
    const params = (new HttpParams())
      .append('fields', 'id,end_user_order_id')
      .append('expand', 'image,order_start_time,order_finish_time,end_user_id,end_user_login');
    this.http.get(environment.apiBasepoint + 'order/' + orderId + '/image', {params: params})
      .subscribe((images: any) => {
        this.orderPhotosLightboxService.open(images.map(image => {
          return {
            imageType: 'end_user_order',
            imageLink: 'data:image/jpeg;base64,' + image.image,
            end_user_order_id: image.end_user_order_id,
            end_user_id: image.end_user_id,
            end_user_login: image.end_user_login,
            order_start_time: image.order_start_time,
            order_finish_time: image.order_finish_time,
          };
        }), 0, true, true);
      });
  }

  mobileApplicationAndItsVersion(data: {
    project_name: string;
    request_os_type: string;
    request_client_version: string;
  }) {
    return {
      iconName: this.getValidValue(data.request_os_type)
      && data.request_os_type.toLowerCase() === 'ios' ? 'ic-store-ios' : 'ic-store-android',
      title: 'Mobile application and its version',
      value: () => {
        return this.getValidValue(data.project_name || data.request_client_version) ?
          this.getValidValue(data.project_name) + ' ' + this.getValidValue(data.request_client_version) : '';
      }
    };
  }

  operatingSystemAndVersion(data: {
    last_request_os_type: string,
    request_os_version: string,
  }) {
    return {
      iconName: this.getValidValue(data.last_request_os_type)
      && data.last_request_os_type.toLowerCase() === 'ios'
        ? 'ic-ios' : 'ic-android',
      title: 'Operating system and version',
      value: () => {
        return this.getValidValue(data.request_os_version);
      }
    };
  }

  phoneManufacturerAndModel(data: {
    request_device_vendor: string
    request_device_model: string
  }) {
    return {
      iconName: 'ic-phone',
      title: 'Phone manufacturer and model',
      value: () => {
        return this.getValidValue(data.request_device_vendor || data.request_device_model) ?
          this.getValidValue(data.request_device_vendor) + ' ' + this.getValidValue(data.request_device_model) : '';
      }
    };
  }

  startTime(start_time_timestamp: number) {
    return {
      title: 'Start time',
      iconName: 'ic_start',
      value: () => this.getValidValue(start_time_timestamp) ? timestampToDateTime(start_time_timestamp) : null,
    };
  }

  finishTime(finish_time_timestamp: number) {
    return {
      title: 'Finish time',
      iconName: 'ic_finish',
      value: () => this.getValidValue(finish_time_timestamp) ? timestampToDateTime(finish_time_timestamp) : null
    };
  }

  distance(mileage: number) {
    return {
      title: 'Mileage',
      iconName: 'ic_distance',
      value: () => {
        const result = this.getValidValue(mileage);
        return result ? result + ' ' + this.translates.instant('km') : '';
      }
    };
  }

  unitName(unit: Unit) {
    return {
      title: 'Name',
      iconName: 'icon-unit-type-' + unit.unit_type_name,
      routerLink: this.routeLibrary.getRoute({
        entity: RouteLibraryEntities.UNIT,
        id: unit.id, action: 'view'
      }),
      value: () => {
        return unit.name + ' ' + unit.unit_model_name;
      }
    };
  }

  unitOwner(unit: Unit) {
    return {
      title: 'Owner',
      iconName: 'ic_' +
        (unit.integrator_id ? AuthorizationService.ROLE_INTEGRATOR : AuthorizationService.ROLE_ADMIN) + '_dark',
      routerLink: this.routeLibrary.getRoute({
        entity: RouteLibraryEntities.USER,
        id: unit.integrator_id ? unit.integrator_id : unit.admin_id,
        action: 'view'
      }),
      value: () => {
        return unit.integrator_id ? unit.integrator_login : unit.admin_login;
      }
    };
  }

  canShowOwner(userId?: number): boolean {
    const currentUser = this.authorizationService.getIdentity();

    return AuthorizationService.calculatedRoleHasRightsNonLessThanAdminsManager(currentUser)
      || currentUser.id === userId;
  }

  owner(user: UserModel) {
    return {
      title: 'Owner',
      iconName: user ? 'ic_' + user.calculatedRole + '_dark' : '',
      routerLink: user ? this.userLinkService.getLink(user) : null,
      value: () => user && user.id && user.login ? user.login : null,
    };
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
